import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Star, Trash2, Bell, Eye, BarChart3, FileText, RefreshCw, Loader2, TrendingUp, Calendar, UserSearch, Newspaper, FileDown, Users } from 'lucide-react';
import axios from 'axios';
import { useWatchlistStore } from '../../../state/watchlistStore';
import { useAuthStore } from '../../../state/authStore';
import MarkdownMessage from '../../../components/ui/markdown-message';
import PageHeader from '../../../components/layout/PageHeader';
import EmptyState from '../../../components/layout/EmptyState';
import SectionNav from '../../../components/layout/SectionNav';
import { portfolioTabsFor } from '../../../nav/publicNav';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const INSIGHT_ICONS = {
  corporate_action: Calendar,
  insider_trade: UserSearch,
  earnings: TrendingUp,
};

export default function WatchlistPage() {
  const navigate = useNavigate();
  const {
    watchlist, insights, loaded, insightsLoading,
    loadWatchlist, removeFromWatchlist, markInsightAsRead, fetchInsights,
    digest, digestLoading, digestError, fetchLatestDigest, generateDigest,
  } = useWatchlistStore();
  const [activeTab, setActiveTab] = useState('watchlist');
  const [teamWatchlists, setTeamWatchlists] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const userId = useAuthStore(s => s.user?.id);
  const userType = useAuthStore(s => s.user?.userType);
  // Only show sub-nav tabs this user can actually open — institutional users
  // bounce to Home on retail-only portfolio routes.
  const subNavTabs = portfolioTabsFor(userType);

  useEffect(() => {
    if (!loaded) loadWatchlist();
  }, [loaded, loadWatchlist]);

  useEffect(() => {
    if (activeTab === 'brief' && !digest && !digestLoading) {
      fetchLatestDigest();
    }
    if (activeTab === 'team') {
      loadTeamWatchlists();
    }
  }, [activeTab]);

  const loadTeamWatchlists = async () => {
    if (!userId) return;
    setTeamLoading(true);
    try {
      const { data: orgs } = await axios.get(`${BACKEND}/api/orgs`, { params: { user_id: userId } });
      const lists = [];
      for (const org of (orgs || []).slice(0, 5)) {
        try {
          const { data } = await axios.get(`${BACKEND}/api/shared-watchlists`, { params: { org_id: org.id } });
          lists.push(...(data || []).map(w => ({ ...w, org_name: org.name })));
        } catch { /* ignore */ }
      }
      setTeamWatchlists(lists);
    } catch { /* ignore */ }
    setTeamLoading(false);
  };

  const unreadInsights = insights.filter((i) => !i.isRead);

  const handleExportDigestPdf = () => {
    const el = document.getElementById('digest-content');
    if (!el) return;
    import('../../../utils/exportPdf').then(({ exportElementAsPdf }) => {
      exportElementAsPdf(el, 'KubAIr Weekly Brief');
    });
  };

  return (
    <div className="space-y-4">
      {subNavTabs.length > 1 && <SectionNav tabs={subNavTabs} />}
      <PageHeader
        title="Watchlist"
        description={`${watchlist.length} stock${watchlist.length !== 1 ? 's' : ''} tracked${unreadInsights.length > 0 ? ` · ${unreadInsights.length} unread insights` : ''}`}
        actions={
          <Button
            onClick={() => navigate('/public/screener')}
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 text-xs"
          >
            <Star className="w-3.5 h-3.5" /> Add from Explorer
          </Button>
        }
      />

      {/* Tabs: Watchlist / Weekly Brief / Team */}
      <div className="flex gap-1 bg-muted/50 p-0.5 rounded-lg w-fit">
        {[{ id: 'watchlist', label: 'Stocks', icon: Star }, { id: 'brief', label: 'Weekly Brief', icon: Newspaper }, { id: 'team', label: 'Team', icon: Users }].map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              activeTab === t.id ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {/* Weekly Brief Tab */}
      {activeTab === 'brief' && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold">Weekly Brief</h3>
                {digest?.generated_at && (
                  <p className="text-[11px] text-muted-foreground">
                    Generated {new Date(digest.generated_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                )}
              </div>
              <div className="flex gap-1.5">
                {digest?.markdown && (
                  <Button size="sm" variant="outline" className="text-xs h-7 gap-1" onClick={handleExportDigestPdf}>
                    <FileDown className="w-3 h-3" /> PDF
                  </Button>
                )}
                <Button
                  size="sm" className="text-xs h-7 gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  disabled={digestLoading}
                  onClick={() => generateDigest()}
                >
                  {digestLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                  {digestLoading ? 'Generating…' : digest ? 'Regenerate' : 'Generate'}
                </Button>
              </div>
            </div>
            {digestError && <p className="text-xs text-red-500">{digestError}</p>}
            {digestLoading && !digest && (
              <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Analyzing your watchlist…</span>
              </div>
            )}
            {digest?.markdown && (
              <div id="digest-content" className="prose-sm">
                <MarkdownMessage content={digest.markdown} />
              </div>
            )}
            {!digest && !digestLoading && watchlist.length > 0 && (
              <div className="text-center py-8 space-y-2">
                <Newspaper className="w-8 h-8 mx-auto text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">Generate an AI-powered weekly brief for your watchlist stocks.</p>
              </div>
            )}
            {watchlist.length === 0 && (
              <div className="text-center py-8 space-y-2">
                <Star className="w-8 h-8 mx-auto text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">Add stocks to your watchlist first to generate a brief.</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Team Watchlists Tab */}
      {activeTab === 'team' && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <h3 className="text-sm font-semibold">Team Watchlists</h3>
            {teamLoading && (
              <div className="flex items-center gap-2 py-6 justify-center text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" /> <span className="text-sm">Loading…</span>
              </div>
            )}
            {!teamLoading && teamWatchlists.length === 0 && (
              <div className="text-center py-8 space-y-2">
                <Users className="w-8 h-8 mx-auto text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">No shared watchlists yet. Create an organization and add team watchlists.</p>
                <Button size="sm" variant="outline" className="text-xs" onClick={() => navigate('/public/org-settings')}>
                  Go to Organization Settings
                </Button>
              </div>
            )}
            {teamWatchlists.map(wl => (
              <div key={wl.id} className="flex items-center justify-between py-2 px-3 rounded-lg border border-border hover:border-emerald-400/50 transition-colors">
                <div>
                  <p className="text-xs font-medium">{wl.name}</p>
                  <p className="text-[10px] text-muted-foreground">{wl.org_name} · {wl.item_count} stocks</p>
                </div>
                <Badge variant="outline" className="text-[10px]">Shared</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {activeTab === 'watchlist' && (<>
      {/* Original watchlist content */}

      {/* Insights Section */}
      {watchlist.length > 0 && (
        <Card className="border-amber-500/20 bg-amber-500/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Bell className="w-4 h-4 text-amber-500" />
              <span className="text-sm font-semibold text-foreground">
                Live Insights {unreadInsights.length > 0 && `(${unreadInsights.length})`}
              </span>
              <Button
                size="sm" variant="ghost"
                className="ml-auto h-7 px-2 text-[11px] text-muted-foreground"
                onClick={() => fetchInsights()}
                disabled={insightsLoading}
              >
                {insightsLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                <span className="ml-1">Refresh</span>
              </Button>
            </div>
            {insightsLoading && insights.length === 0 && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              </div>
            )}
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {insights.slice(0, 20).map((insight) => {
                const Icon = INSIGHT_ICONS[insight.type] || Bell;
                return (
                  <div
                    key={insight.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border border-border/50 ${
                      insight.isRead ? 'bg-background/50 opacity-60' : 'bg-background'
                    }`}
                  >
                    <Icon className="w-4 h-4 mt-0.5 text-amber-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <span className="font-semibold text-sm text-foreground">{insight.symbol}</span>
                        <Badge variant="outline" className={`text-[10px] ${
                          insight.severity === 'high' ? 'border-red-400 text-red-500' :
                          insight.severity === 'medium' ? 'border-amber-400 text-amber-500' :
                          'border-border text-muted-foreground'
                        }`}>{insight.severity}</Badge>
                        <Badge variant="outline" className="text-[10px]">{(insight.type || '').replace(/_/g, ' ')}</Badge>
                        {insight.date && (
                          <span className="text-[10px] text-muted-foreground">{new Date(insight.date).toLocaleDateString('en-IN')}</span>
                        )}
                      </div>
                      <p className="text-sm text-foreground/80">{insight.title}</p>
                      {insight.description && (
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{insight.description}</p>
                      )}
                    </div>
                    {!insight.isRead && (
                      <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-foreground shrink-0" onClick={() => markInsightAsRead(insight.id)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                );
              })}
              {insights.length === 0 && !insightsLoading && (
                <p className="text-xs text-muted-foreground text-center py-4">No recent insights for your watchlist.</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Watchlist Cards */}
      {watchlist.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {watchlist.map((item) => {
            const sym = item.symbol;
            const name = item.company_name || item.companyName || sym;
            const addedDate = item.added_at || item.addedAt;
            return (
              <Card
                key={item.id}
                className="group border-border/50 hover:border-emerald-500/30 hover:shadow-lg hover:shadow-emerald-500/5 transition-all cursor-pointer"
                onClick={() => navigate(`/public/india-company/${sym}`)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-sm text-foreground truncate">{name}</h3>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs font-mono font-bold text-emerald-600 dark:text-emerald-400">{sym}</span>
                        <span className="text-xs text-muted-foreground">{item.exchange || 'NSE'}</span>
                      </div>
                    </div>
                    <Star className="w-4 h-4 fill-amber-400 text-amber-400 shrink-0" />
                  </div>

                  {addedDate && (
                    <p className="text-[10px] text-muted-foreground mb-3">
                      Added {new Date(addedDate).toLocaleDateString()}
                    </p>
                  )}

                  <div className="flex items-center gap-1.5 pt-2 border-t border-border/50" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => navigate(`/public/india-company/${sym}`)}
                      className="h-7 px-2 text-[11px] text-muted-foreground hover:text-foreground gap-1"
                    >
                      <BarChart3 className="w-3 h-3" /> Analyze
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => navigate(`/public/india-company/${sym}/pitch`)}
                      className="h-7 px-2 text-[11px] text-muted-foreground hover:text-foreground gap-1"
                    >
                      <FileText className="w-3 h-3" /> Pitch
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeFromWatchlist(item.id)}
                      className="h-7 px-2 text-[11px] text-red-500 hover:text-red-600 hover:bg-red-500/10 ml-auto gap-1"
                    >
                      <Trash2 className="w-3 h-3" /> Remove
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <EmptyState
          icon={Star}
          title="No stocks in your watchlist"
          description="Browse the India Explorer to find stocks to watch"
          action={{ label: 'Explore Stocks', href: '/public/screener' }}
        />
      )}
      </>)}
    </div>
  );
}
